import React from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes } from 'react-router-dom';
import Dashboard from 'src/pages/Dashboard';
import Outbound from 'src/pages/Outbound';
import AiChat from 'src/pages/AiChat';
import Returns from 'src/pages/Returns';
import Contact from 'src/pages/Contact';
import AdvancedReports from 'src/pages/AdvancedReports';
import WorkflowCreate from 'src/pages/WorkflowCreate';
import WorkflowOverview from 'src/pages/WorkflowOverview';
import Apps from 'src/pages/Apps';
import Settings from 'src/pages/Settings';
import 'react-toastify/dist/ReactToastify.css';
import { ToastContainer } from 'react-toastify';
import FlowAiWidget from 'src/components/layouts/FlowAiWidget/FlowAiWidget';
import { Nav } from './components/layouts/Nav/Nav';
// import CarrierAnalytics from './pages/CarrierAnalytics';
import CarrierAnalyticsDetail from './pages/CarrierAnalyticsDetail';
import CountriesAndGroups from './pages/CountriesAndGroups';
import Inventory from './pages/Inventory';
import Inbound from './pages/Inbound/Inbound';
import Integrations from './pages/Integrations';
import InboundCreateData from './pages/Inbound/InboundCreateData';
import InboundCreateSummary from './pages/Inbound/InboundCreateSummary';
import InboundCreateConfirm from './pages/Inbound/InboundCreateConfirm';
import { InboundFormContextProvider } from './pages/Inbound/InboundFormContext';
import { CaseOverlay } from './pages/CaseOverlay';
import { ContactOverlay } from './pages/ContactOverlay';
import { RouteOverlay } from './components/layouts/RouteOverlay/RouteOverlay';
import { CreateContactOverlay } from './pages/CreateContactOverlay';
import CasesOverview from './pages/CasesOverview';
import ClaimsOverview from './pages/ClaimsOverview';
import { ClaimOverlay } from './pages/ClaimOverlay';
import IntegrationsDashboard from './pages/IntegrationsDashboard';
import IntegrationDetails from './pages/IntegrationDetails';
import ReturnsCreateTracking from './pages/Returns/ReturnsCreateTracking';
import ReturnsCreateProducts from './pages/Returns/ReturnsCreateProducts';
import ReturnsCreateSummary from './pages/Returns/ReturnsCreateSummary';
import ReturnsCreateDone from './pages/Returns/ReturnsCreateDone';
import { ReturnsFormContextProvider } from './pages/Returns/ReturnsFormContext';
import CarrierList from './pages/CarrierList';
import { ReturnOverlay } from './pages/ReturnOverlay/ReturnOverlay';
import { CountryGroupOverlay } from './pages/CountryGroupOverlay';
import { OutboundOverlay } from './components/layouts/OutboundOverlay/OutboundOverlay';
import ProductOverlay from './components/layouts/ProductOverlay/ProductOverlay';
import CreateProductOverlay from './components/layouts/CreateProductOverlay/CreateProductOverlay';
import CreateSaleOrderOverlay from './components/layouts/CreateSaleOrderOverlay/CreateSaleOrderOverlay';
import UserOverlay from './pages/UserOverlay';
import ImportData from './pages/ImportData';
import AdvancedReportType from './pages/AdvancedReportType';
import WorkflowView from './pages/WorkflowView';
import CarrierAnalytics from './pages/CarrierAnalytics';
import Statistics from './pages/Statistics';
import IntegrationsMappings from './pages/Integrations/IntegrationsMappings';
import Forecasts from './pages/Forecasts/Forecasts';
import Invoices from './pages/Invoices/Invoices';
import InvoiceOverlay from './pages/Invoices/InvoiceOverlay';
import CommandPalette from './components/layouts/CommandPalette/CommandPalette';
import ChangelogGate from './components/layouts/ChangelogGate/ChangelogGate';
import ErrorBoundary from './components/layouts/ErrorBoundary/ErrorBoundary';
import BugReportModal from './components/modals/BugReportModal/BugReportModal';
import { BugReportProvider } from './providers/BugReportProvider';

// Make sure to keep this in sync with the routes below
export function getDetailViewUrl(model: string, id: number): string | null {
  if (model === 'res.partner') {
    return `/contact/${id}`;
  }

  if (model === 'product.product') {
    return `/warehouse/inventory/${id}`;
  }

  if (model === 'purchase.order') {
    return `/warehouse/inbound/${id}`;
  }

  if (model === 'sale.order') {
    return `/outbound/${id}`;
  }

  if (model === 'project.task') {
    return `/cases/${id}`;
  }

  if (model === 'stock.picking') {
    return `/returns/${id}`;
  }

  if (model === 'df.changelog') {
    return `?changelog=${id}`;
  }

  return null;
}

function Layout() {
  const { t } = useTranslation();
  return (
    <BugReportProvider>
      <div className="flex h-dvh">
        <CommandPalette />
        <ChangelogGate />
        <BugReportModal />
        <Nav />
        <div className="flex flex-col flex-1 overflow-y-auto main-content-wrapper">
          <div className="relative flex-grow text-full surface-base flex flex-col w-full">
            <ErrorBoundary>
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="warehouse/inventory" element={<Inventory />}>
                  <Route
                    path=":id"
                    element={
                      <RouteOverlay isExtended>
                        <ProductOverlay />
                      </RouteOverlay>
                    }
                  />
                  <Route
                    path="create"
                    element={
                      <RouteOverlay isExtended>
                        <CreateProductOverlay />
                      </RouteOverlay>
                    }
                  />
                  <Route
                    path="import"
                    element={
                      <RouteOverlay isExtended>
                        <ImportData context="product_inventory" />
                      </RouteOverlay>
                    }
                  />
                </Route>
                <Route path="warehouse/inbound" element={<Inbound />} />
                <Route path="cases" element={<CasesOverview />}>
                  <Route path=":id" element={<CaseOverlay mode="view" />} />
                  <Route path="create" element={<CaseOverlay mode="create" />} />
                </Route>
                <Route path="cases/claims" element={<ClaimsOverview />}>
                  <Route path=":id" element={<ClaimOverlay mode="view" />} />
                  <Route path="create" element={<ClaimOverlay mode="create" />} />
                </Route>
                <Route
                  path="warehouse/inbound/*"
                  element={
                    <InboundFormContextProvider>
                      <Routes>
                        <Route path="create/data" element={<InboundCreateData />} />
                        <Route path="import/data" element={<ImportData context="inbound" />} />
                        <Route path="create/summary" element={<InboundCreateSummary />} />
                        <Route path=":id" element={<InboundCreateConfirm />} />
                      </Routes>
                    </InboundFormContextProvider>
                  }
                />
                <Route path="outbound" element={<Outbound />}>
                  <Route
                    path=":id"
                    element={
                      <RouteOverlay isExtended>
                        <OutboundOverlay />
                      </RouteOverlay>
                    }
                  />
                  <Route
                    path="create"
                    element={
                      <RouteOverlay isExtended>
                        <CreateSaleOrderOverlay />
                      </RouteOverlay>
                    }
                  />
                  <Route
                    path="import"
                    element={
                      <RouteOverlay isExtended>
                        <ImportData context="outbound" />
                      </RouteOverlay>
                    }
                  />
                </Route>
                <Route path="ai-chat" element={<AiChat />} />
                <Route path="integrations/connectors" element={<Integrations />} />
                <Route path="integrations/dashboard" element={<IntegrationsDashboard />} />
                <Route path="/integrations/mappings" element={<IntegrationsMappings />} />
                <Route path="/integrations/connectors/:id" element={<IntegrationDetails />} />
                <Route
                  path="returns/*"
                  element={
                    <ReturnsFormContextProvider>
                      <Routes>
                        <Route path="create/tracking" element={<ReturnsCreateTracking />} />
                        <Route path="create/products" element={<ReturnsCreateProducts />} />
                        <Route path="create/summary" element={<ReturnsCreateSummary />} />
                        <Route path="create/done" element={<ReturnsCreateDone />} />
                      </Routes>
                    </ReturnsFormContextProvider>
                  }
                />
                <Route path="returns" element={<Returns />}>
                  <Route path=":id" element={<ReturnOverlay />} />
                </Route>
                <Route path="invoices" element={<Invoices />}>
                  <Route path=":id" element={<InvoiceOverlay />} />
                </Route>
                <Route path="advanced-reports" element={<AdvancedReports />} />
                <Route path="advanced-reports/:type" element={<AdvancedReportType />} />
                <Route path="statistics" element={<Statistics />} />

                <Route path="workflows" element={<WorkflowOverview />} />
                <Route path="workflows/create" element={<WorkflowCreate />} />
                <Route path="workflows/:id" element={<WorkflowView />} />
                <Route path="apps" element={<Apps />} />
                <Route path="settings/contacts" element={<Settings />}>
                  <Route
                    path=":id"
                    element={
                      <RouteOverlay>
                        <ContactOverlay />
                      </RouteOverlay>
                    }
                  />
                  <Route
                    path="create"
                    element={
                      <RouteOverlay>
                        <CreateContactOverlay />
                      </RouteOverlay>
                    }
                  />
                </Route>
                <Route path="settings" element={<Settings />}>
                  <Route
                    path="create"
                    element={
                      <RouteOverlay isExtended>
                        <UserOverlay mode="create" />
                      </RouteOverlay>
                    }
                  />
                  <Route
                    path="edit/:id"
                    element={
                      <RouteOverlay isExtended>
                        <UserOverlay mode="view" />
                      </RouteOverlay>
                    }
                  />
                </Route>
                <Route path="carrier-analytics/dashboard" element={<CarrierAnalytics />} />
                <Route path="carrier-analytics/:name" element={<CarrierAnalyticsDetail />} />
                <Route path="carrier-analytics/countries-and-groups" element={<CountriesAndGroups />}>
                  <Route
                    path="edit/:id"
                    element={
                      <RouteOverlay title={t('general.edit_group')}>
                        <CountryGroupOverlay editMode />
                      </RouteOverlay>
                    }
                  />
                </Route>
                <Route
                  path="carrier-analytics/countries-and-groups/add"
                  element={
                    <RouteOverlay title={t('general.add_new_group')}>
                      <CountryGroupOverlay />
                    </RouteOverlay>
                  }
                />
                <Route path="carrier-analytics/carrier-list" element={<CarrierList />} />
                <Route path="forecasts" element={<Forecasts />} />
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
                <Route path="contact" element={<Contact />}>
                  <Route
                    path=":id"
                    element={
                      <RouteOverlay>
                        <ContactOverlay />
                      </RouteOverlay>
                    }
                  />
                  <Route
                    path="create"
                    element={
                      <RouteOverlay>
                        <CreateContactOverlay />
                      </RouteOverlay>
                    }
                  />
                </Route>
                <Route path="/integrations/connectors/:id" element={<IntegrationDetails />} />
              </Routes>
            </ErrorBoundary>
            {/* Inside the page content: the widget's spacer keeps the page end free under the launcher */}
            <FlowAiWidget />
          </div>
        </div>
        <ToastContainer autoClose={2000} position="bottom-right" toastClassName="typo-text-14" />
      </div>
    </BugReportProvider>
  );
}

export default Layout;
